/**
 * Dashboard plane panels — the QUADRANT redesign (2026-07-22).
 *
 * The owner rejected both the boxed 2×2 mini-grids AND the borderless
 * "rows-per-plane" band as too flat / too sparse. The approved model gives the
 * four load-bearing domains — Workflow · Pipeline ETL · App e portale · AI — a
 * PANEL each (a 2×2 quadrant) with clear internal hierarchy: one protagonist
 * number, small satellite stats on the same baseline, and a footer strip
 * (sparkline / budget bar / contextual warning). The remaining planes
 * (Dati · Notifiche · Gateway) live in a slim secondary band — present, but a
 * lower visual weight.
 *
 * Still PRESENTATIONAL: each panel receives its plane's summary payload (or
 * `undefined` while loading) from the page, which owns the permission-gated
 * query. Exports keep the historical `*Card` names + the `dashboard-card-*`
 * testids so the page wiring + gating tests are untouched — WorkflowCard keeps
 * `dashboard-card-integration` (it rides the overview, no plane permission).
 */
import {
  Bot,
  ChevronRight,
  Database,
  LayoutGrid,
  BellRing,
  RadioTower,
  UserPlus,
  Waypoints,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type {
  AiSummary,
  AppsSummary,
  DashboardOverview,
  DataSummary,
  EtlHourBucket,
  EtlSummary,
  GatewaysSummary,
  HourBucket,
  OpsSummary,
} from "@/lib/api-dashboard";

/** 1.2k / 3.4M style compaction for token counts. */
function compact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

// --- small building blocks -------------------------------------------------

/** A satellite stat: a small emphasised value + a muted label, on one line. */
function Stat({
  value,
  label,
  tone,
}: {
  value: string | number;
  label: string;
  tone?: "danger" | "warning";
}) {
  const valueClass =
    tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <span className="whitespace-nowrap text-xs">
      <span className={"font-semibold tabular-nums " + valueClass}>{value}</span>{" "}
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

/** The panel frame for a plane quadrant. Header (icon · title · right meta ·
 *  chevron) → hero number → satellites → optional footer. The whole panel is
 *  a link to the plane. */
function Quadrant({
  title,
  icon: Icon,
  to,
  testid,
  meta,
  loading,
  children,
}: {
  title: string;
  icon: LucideIcon;
  to: string;
  testid: string;
  meta?: ReactNode;
  loading: boolean;
  children?: ReactNode;
}) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex flex-col rounded-xl border border-border bg-elevated px-4 py-3.5 transition hover:border-muted-foreground/30"
    >
      <div className="mb-2.5 flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-sm font-medium">{title}</span>
        {meta !== undefined && (
          <span className="ml-auto text-xs text-muted-foreground">{meta}</span>
        )}
        <ChevronRight
          className={
            "h-4 w-4 shrink-0 text-muted-foreground/40 transition group-hover:text-muted-foreground" +
            (meta !== undefined ? "" : " ml-auto")
          }
          strokeWidth={1.5}
        />
      </div>
      {loading ? <div className="text-sm text-muted-foreground">—</div> : children}
    </Link>
  );
}

/** The protagonist number + its unit label. */
function Hero({ value, unit }: { value: string | number; unit: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[1.75rem] font-semibold leading-none tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{unit}</span>
    </div>
  );
}

/** A tiny bar sparkline; each bar is `{total, failed}` — the failed portion is
 *  drawn in danger. Purely decorative (aria-hidden). */
function Sparkline({
  bars,
  className = "",
}: {
  bars: { total: number; failed: number }[];
  className?: string;
}) {
  const max = Math.max(1, ...bars.map((b) => b.total));
  return (
    <div className={"flex h-7 items-end gap-px " + className} aria-hidden="true">
      {bars.map((b, i) => {
        const h = Math.max(b.total > 0 ? 8 : 2, Math.round((b.total / max) * 100));
        const failedPart = b.total > 0 ? Math.round((b.failed / b.total) * 100) : 0;
        return (
          <div key={i} className="flex-1" style={{ height: `${h}%` }}>
            {failedPart > 0 && (
              <div className="w-full bg-danger" style={{ height: `${failedPart}%` }} />
            )}
            <div className="w-full bg-success/55" style={{ height: `${100 - failedPart}%` }} />
          </div>
        );
      })}
    </div>
  );
}

/** A thin progress bar (AI budget consumption). */
function BudgetBar({ spent, budget }: { spent: number; budget: number }) {
  const pct = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
  const tone = pct >= 90 ? "bg-danger" : pct >= 70 ? "bg-warning" : "bg-accent";
  const { t } = useTranslation();
  return (
    <div className="mt-3">
      <div className="h-1 overflow-hidden rounded-full bg-border">
        <div className={"h-full " + tone} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        {t("dashboard.ai.budgetUsed", "{{pct}}% del budget mensile", { pct })}
      </div>
    </div>
  );
}

// --- quadrant panels (the 4 load-bearing planes) ---------------------------

/** Workflow plane. Rides the overview (no plane permission) so it keeps the
 *  historical `dashboard-card-integration` testid + is always present. */
export function IntegrationCard({
  overview,
  buckets,
}: {
  overview?: DashboardOverview;
  buckets?: HourBucket[];
}) {
  const { t } = useTranslation();
  const total = overview
    ? overview.runs_24h.succeeded +
      overview.runs_24h.failed +
      overview.runs_24h.running +
      overview.runs_24h.cancelled
    : 0;
  return (
    <Quadrant
      title={t("dashboard.plane.workflow", "Workflow")}
      icon={Workflow}
      to="/workflow-runs"
      testid="dashboard-card-integration"
      meta={
        overview
          ? t("dashboard.workflow.active", "{{a}}/{{b}} attivi", {
              a: overview.workflows_enabled,
              b: overview.workflows_total,
            })
          : undefined
      }
      loading={!overview}
    >
      {overview && (
        <>
          <Hero value={total} unit={t("dashboard.workflow.runs", "esecuzioni 24h")} />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <Stat
              value={overview.runs_24h.failed}
              label={t("dashboard.workflow.failed", "fallite")}
              tone={overview.runs_24h.failed > 0 ? "danger" : undefined}
            />
            <Stat
              value={overview.runs_24h.running}
              label={t("dashboard.workflow.running", "in corso")}
              tone={overview.runs_24h.running > 0 ? "warning" : undefined}
            />
            <Stat
              value={overview.webhook_endpoints}
              label={t("dashboard.kpi.webhookEndpoints", "endpoint webhook")}
            />
          </div>
          {buckets && buckets.length > 0 && (
            <Sparkline
              className="mt-3"
              bars={buckets.map((b) => ({
                total: b.succeeded + b.failed + b.running + b.cancelled,
                failed: b.failed,
              }))}
            />
          )}
        </>
      )}
    </Quadrant>
  );
}

/** ETL plane — the engine twin of the Workflow quadrant. */
export function EtlCard({ data }: { data?: EtlSummary }) {
  const { t } = useTranslation();
  const total = data
    ? data.runs_24h.succeeded +
      data.runs_24h.failed +
      data.runs_24h.running +
      data.runs_24h.pending +
      data.runs_24h.cancelled
    : 0;
  return (
    <Quadrant
      title={t("dashboard.plane.etl", "Pipeline ETL")}
      icon={Waypoints}
      to="/etl"
      testid="dashboard-card-etl"
      meta={
        data
          ? t("dashboard.etl.enabled", "{{a}}/{{b}} abilitate", {
              a: data.pipelines_enabled,
              b: data.pipelines_total,
            })
          : undefined
      }
      loading={!data}
    >
      {data && (
        <>
          <Hero value={total} unit={t("dashboard.etl.runs", "run 24h")} />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <Stat
              value={data.runs_24h.failed}
              label={t("dashboard.etl.failed", "falliti")}
              tone={data.runs_24h.failed > 0 ? "danger" : undefined}
            />
            <Stat
              value={data.runs_24h.pending}
              label={t("dashboard.etl.pending", "in coda")}
              tone={data.runs_24h.pending > 0 ? "warning" : undefined}
            />
            <Stat
              value={compact(data.rows_out_24h)}
              label={t("dashboard.etl.rowsOut", "righe scritte")}
            />
          </div>
          {data.buckets.length > 0 && (
            <Sparkline
              className="mt-3"
              bars={data.buckets.map((b: EtlHourBucket) => ({
                total: b.total,
                failed: b.failed,
              }))}
            />
          )}
        </>
      )}
    </Quadrant>
  );
}

/** App & Portal plane. Hero = active apps (`2/3`). */
export function AppsCard({ data }: { data?: AppsSummary }) {
  const { t } = useTranslation();
  const pending = data?.attention.pending_registrations ?? 0;
  return (
    <Quadrant
      title={t("dashboard.plane.apps", "App e portale")}
      icon={LayoutGrid}
      to="/apps"
      testid="dashboard-card-apps"
      meta={data ? t("dashboard.apps.formsMeta", "{{n}} form", { n: data.forms_total }) : undefined}
      loading={!data}
    >
      {data && (
        <>
          <Hero
            value={`${data.apps_enabled}/${data.apps_total}`}
            unit={t("dashboard.apps.active", "app attive")}
          />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <Stat
              value={data.portal_users_active_24h}
              label={t("dashboard.apps.portalUsers", "utenti portale (24h)")}
            />
            <Stat
              value={data.data_writes_24h}
              label={t("dashboard.apps.dataWrites", "scritture (24h)")}
            />
          </div>
          {pending > 0 && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-warning">
              <UserPlus className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("dashboard.apps.pending", "{{n}} registrazione in attesa", { n: pending })}
            </div>
          )}
        </>
      )}
    </Quadrant>
  );
}

/** AI plane. Hero = 30-day cost (`$1.50`) + budget bar. */
export function AiCard({ data }: { data?: AiSummary }) {
  const { t } = useTranslation();
  const empty = data && data.turns === 0 && data.total_cost_usd === 0;
  return (
    <Quadrant
      title={t("dashboard.plane.ai", "AI")}
      icon={Bot}
      to="/ai/executions"
      testid="dashboard-card-ai"
      meta={
        data ? t("dashboard.ai.window", "ultimi {{days}} giorni", { days: data.days }) : undefined
      }
      loading={!data}
    >
      {data &&
        (empty ? (
          <div className="text-sm text-muted-foreground">
            {t("dashboard.ai.noUsage", "Nessun utilizzo")}
          </div>
        ) : (
          <>
            <Hero
              value={`$${data.total_cost_usd.toFixed(2)}`}
              unit={t("dashboard.ai.costUnit", "costo")}
            />
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <Stat value={compact(data.total_tokens)} label={t("dashboard.ai.tokens", "token")} />
              <Stat value={data.turns} label={t("dashboard.ai.turns", "turni")} />
              <Stat
                value={data.assistant_threads_total}
                label={t("dashboard.ai.sessions", "conversazioni")}
              />
            </div>
            {data.monthly_budget_usd > 0 && (
              <BudgetBar spent={data.spend_month_usd} budget={data.monthly_budget_usd} />
            )}
          </>
        ))}
    </Quadrant>
  );
}

// --- secondary band (slim rows) --------------------------------------------

/** A compact secondary card (Dati · Notifiche · Gateway). One line of inline
 *  stats — lower visual weight than the quadrants. */
function MiniCard({
  title,
  icon: Icon,
  to,
  testid,
  loading,
  children,
}: {
  title: string;
  icon: LucideIcon;
  to: string;
  testid: string;
  loading: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex flex-col rounded-xl border border-border bg-surface px-4 py-3 transition hover:border-muted-foreground/30"
    >
      <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
        <span>{title}</span>
        <ChevronRight
          className="ml-auto h-3.5 w-3.5 text-muted-foreground/40 transition group-hover:text-muted-foreground"
          strokeWidth={1.5}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {loading ? <span className="text-xs text-muted-foreground">—</span> : children}
      </div>
    </Link>
  );
}

export function DataCard({ data }: { data?: DataSummary }) {
  const { t } = useTranslation();
  return (
    <MiniCard
      title={t("dashboard.plane.data", "Dati e conoscenza")}
      icon={Database}
      to="/datasets"
      testid="dashboard-card-data"
      loading={!data}
    >
      {data && (
        <>
          <Stat
            value={`${data.datasets_published}/${data.datasets_total}`}
            label={t("dashboard.data.datasets", "dataset")}
          />
          <Stat value={data.sources_total} label={t("dashboard.data.sources", "sorgenti")} />
          <Stat
            value={data.knowledge_bases_total}
            label={t("dashboard.data.knowledgeBases", "KB")}
          />
        </>
      )}
    </MiniCard>
  );
}

export function OpsCard({ data }: { data?: OpsSummary }) {
  const { t } = useTranslation();
  return (
    <MiniCard
      title={t("dashboard.plane.ops", "Notifiche e allerte")}
      icon={BellRing}
      to="/alerts"
      testid="dashboard-card-ops"
      loading={!data}
    >
      {data && (
        <>
          <Stat
            value={data.open_alerts_24h}
            label={t("dashboard.ops.alerts", "allerte")}
            tone={data.open_alerts_24h > 0 ? "warning" : undefined}
          />
          <Stat
            value={data.dead_letters}
            label={t("dashboard.ops.deadLetters", "dead-letter")}
            tone={data.dead_letters > 0 ? "danger" : undefined}
          />
          <Stat
            value={data.notifications_24h}
            label={t("dashboard.ops.notifications", "notifiche")}
          />
        </>
      )}
    </MiniCard>
  );
}

export function GatewaysCard({ data }: { data?: GatewaysSummary }) {
  const { t } = useTranslation();
  return (
    <MiniCard
      title={t("dashboard.plane.gateways", "Gateway")}
      icon={RadioTower}
      to="/gateways"
      testid="dashboard-card-gateways"
      loading={!data}
    >
      {data && (
        <>
          <Stat
            value={`${data.online}/${data.total}`}
            label={t("dashboard.gateways.online", "online")}
          />
          <Stat
            value={data.offline}
            label={t("dashboard.gateways.offline", "offline")}
            tone={data.offline > 0 ? "danger" : undefined}
          />
          <Stat
            value={data.revoked}
            label={t("dashboard.gateways.revoked", "revocati")}
            tone={data.revoked > 0 ? "warning" : undefined}
          />
        </>
      )}
    </MiniCard>
  );
}
